import { writable } from 'svelte/store';

// Create a store for the current step
export const currentStep = writable(0);

// Create a store for survey responses
export const surveyStore = writable({});

export const recommendationStore = writable(null);

// Answer ID to value mappings based on API response data
// Update answerMappings for the new question options
export const answerMappings = {
  // Question 2: How many games do you want access to?
  attendance: {
    'a': '1-2',
    'b': '3-4',
    'c': '5-6'
  },
  // Question 3: Where do you typically like to sit?
  seating: {
    'a': 'loudville',
    'b': 'club',
    'c': 'lower',
    'd': 'premium'  // Updated to be premium seating
  },
  // Question 5: How important is playoff ticket access to you?
  // Question 5: How important is playoff ticket access to you?
playoffImportance: {
  'a': 'want-membership-seats',
  'b': 'want-guaranteed-seats',
  'c': 'want-some-tickets',
  'd': 'dont-care'
},
  // Question 4: Who do you typically attend with?
  companions: {
    'a': 'family',
    'b': 'friends',
    'c': 'colleagues',
    'd': 'partner',
    'e': 'myself'
  },
  // Question 1: What most inspires you to explore Cavs United today?
  inspiration: {
    'a': 'memories',
    'b': 'experience',
    'c': 'community',
    'd': 'tradition'
  },
  // New mapping for seat count question
  seatCount: {
    'a': '1',
    'b': '2',
    'c': '3',
    'd': '4'
  },
  // Question 3.5: Would you like an elevated experience including food and drink?
  // Only shown when Loudville ('a') or Lower Bowl ('c') seating is selected.
  elevatedExperience: {
    'a': 'yes',
    'b': 'no'
  }
};

// Update answerTextMappings as well
export const answerTextMappings = {
  // Question 1
  inspiration: {
    'a': 'Create lifelong memories with family/friends',
    'b': 'Elevate my Cavs experience',
    'c': 'Join a passionate community of fans',
    'd': 'Continue a family tradition'
  },
  // Question 2 
  attendance: {
    'a': '1-2 games',
    'b': '3-4 games',
    'c': '5-6 games'
  },
  // Question 3
  seating: {
    'a': 'Loudville (Upper Level)',
    'b': 'Mezzanine/Club Sections',
    'c': 'Lower Level',
    'd': 'Premium Seating (Floor, Club Access, Suites)'
  },
  // Question 4
  companions: {
    'a': 'Family',
    'b': 'Friends',
    'c': 'Work Colleagues/Clients',
    'd': 'Significant Other',
    'e': 'I like to go by myself'
  },
  // Question 6
  // Question 6
  playoffImportance: {
    'a': 'I want my membership seats for Playoff games',
    'b': 'I want guaranteed seats for the Playoffs, but don\'t care where they are located',
    'c': 'I don\'t need to go to every Playoff game, but want access to some tickets',
    'd': 'I don\'t really care about Playoff tickets'
  },
  // Question 7 (perks)
  perks: {
    'a': 'Discounts on merchandise and concessions',
    'b': 'Exclusive events and experiences',
    'c': 'Priority access to playoff tickets',
    'd': 'Dedicated member specialist',
    'e': 'No fees on additional tickets',
    'f': 'Priority access to concerts and other Rocket Arena events',
    'g': 'Flexibility to exchange games for others'
  },
  seatCount: {
    'a': '1 seat',
    'b': '2 seats',
    'c': '3 seats',
    'd': '4 seats'
  },
  // Question 3.5
  elevatedExperience: {
    'a': "Yes, that's important to me",
    'b': "No, that's not a priority"
  }
};

// Seating answer IDs that unlock the elevated food & drink club experience.
// Loudville ('a') → Gilly's Club, Lower Bowl ('c') → Caesar's Club.
export function elevatedExperienceApplies(seatingId) {
  return seatingId === 'a' || seatingId === 'c';
}

// Resolve the premium club surfaced in the recommendation from the seating +
// elevated-experience answers. Returns null when it does not apply.
// NOTE: these exact strings are written to SFMC as `addedValue` — changing them
// changes reported data, so keep them stable and map display/links off them.
export function getElevatedClub(seatingId, elevatedId) {
  // 'a' on the elevated question = "Yes, that's important to me"
  if (elevatedId !== 'a') return null;
  if (seatingId === 'a') return "Gilly's Club";  // Loudville
  if (seatingId === 'c') return "Caesar's Club"; // Lower Bowl
  return null;
}

// Public Rocket Arena pages for each elevated club, linked from the
// recommendation callout and the emailed recommendation.
export const ELEVATED_CLUB_URLS = {
  "Gilly's Club": "https://www.rocketarena.com/premium/locations/gillys-clubhouse",
  "Caesar's Club": "https://www.rocketarena.com/premium/locations/caesars-rewards-club"
};

export function getElevatedClubUrl(club) {
  return ELEVATED_CLUB_URLS[club] || null;
}

// Function to initialize the survey with default values
export function initializeSurvey() {
  surveyStore.set({});
  currentStep.set(0);
}

// Function to reset survey to beginning
export function resetSurvey() {
  // Clear all survey data
  surveyStore.set({});
  // Return to the first step (or possibly the intro step)
  currentStep.set(0);
  
  // Optional: Scroll to top of the survey container
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

// Helper function to translate answer IDs to descriptive values
export function translateAnswerId(questionType, answerId) {
  // Check if mapping exists for this question type
  if (!answerMappings[questionType]) {
    console.log(`No mapping found for question type: ${questionType}`);
    return answerId;
  }
  
  // Check if answer ID exists in the mapping
  if (!answerMappings[questionType][answerId]) {
    console.log(`No mapping found for answer ID: ${answerId} in question type: ${questionType}`);
    return answerId;
  }
  
  return answerMappings[questionType][answerId];
}

// Helper function to determine recommendation based on answers
// Sold out flags can be set dynamically based on backend data
export let soldOutFlags = {
  Full: true,
  Half: false,
  Quarter: false
};

// Helper function to determine recommendation based on answers with sold out fallback
export function calculateRecommendation(answers) {
  console.log('Raw survey answers:', answers);
  
  if (!answers) {
    console.error('No survey answers provided to calculateRecommendation');
    return 'Quarter / Loudville'; // Default fallback
  }

  const attendanceId = answers.attendance || answers.q2 || answers['multi-choice-2'] || '';
  const seatingId = answers.seating || answers.q3 || answers['seating-3'] || '';
  const playoffImportanceId = answers.playoffImportance || answers.q5 || answers['multi-choice-5'] || '';

  let attendance = '1-2';
  let seating = 'loudville';
  let playoffImportance = 'want-some-tickets';

  try {
    attendance = translateAnswerId('attendance', attendanceId) || attendance;
    seating = translateAnswerId('seating', seatingId) || seating;
    playoffImportance = translateAnswerId('playoffImportance', playoffImportanceId) || playoffImportance;
  } catch (error) {
    console.error('Error translating answer IDs:', error);
  }

  // Determine initial recommendation
  let recommended = '';
  if (seating === 'premium') {
    recommended = 'Premium';
  } else if (attendance === '5-6') {
    recommended = seating === 'lower' ? 'Full / Lower' : seating === 'club' ? 'Full / Club' : 'Full / Loudville';
  } else if (attendance === '3-4' || (attendance === '1-2' && (playoffImportance === 'want-membership-seats' || playoffImportance === 'want-guaranteed-seats'))) {
    recommended = seating === 'lower' ? 'Half / Lower' : seating === 'club' ? 'Half / Club' : 'Half / Loudville';
  } else {
    recommended = seating === 'lower' ? 'Quarter / Lower' : seating === 'club' ? 'Quarter / Club' : 'Quarter / Loudville';
  }

  // Check soldOutFlags and fallback if necessary
  const levels = ['Full', 'Half', 'Quarter']; // Order of fallback
  let [level, loc] = recommended.split('/').map(s => s.trim());
  
  if (level !== 'Premium') {
    for (let i = levels.indexOf(level); i < levels.length; i++) {
      if (!soldOutFlags[levels[i]]) {
        level = levels[i];
        break;
      }
    }
    recommended = `${level} / ${loc}`;
  }

  return recommended;
}

// Convert recommendation code to human-readable format
export function formatRecommendation(code) {
  console.log('Formatting recommendation code:', code);

  // Handle undefined or null input
  if (!code) {
    console.error('No code provided to formatRecommendation');
    return {
      packageName: "Quarter Loudville Membership",
      details: {
        games: '1 Preseason, 10 Regular Season',
        pricing: 'From $32/mo per seat with 12 monthly payments and 0% interest* Or from $375 total per seat',
        fullMonthlyPrice: 106,
        discountMonthlyPrice: 101,
        fullSeatPrice: 1274,
        discountSeatPrice: 1213,
        savings: '12% over Single Game Ticket Pricing',
        playoffAccess: 'Early Access to Purchase Playoff Tickets at the discounted member rate',
        benefits: 'Ticket Exchange & two selected benefits'
      }
    };
  }

  // Parse the code parts
  let packageType, level;

  if (code.includes('/')) {
    [packageType, level] = code.split('/').map(s => s.trim());
  } else if (code.includes(' ')) {
    const parts = code.split(' ');
    packageType = parts[0].trim();
    level = parts.length > 1 ? parts.slice(1).join(' ').trim() : null;
  } else {
    packageType = code.trim();
    level = '';
  }

  // Ensure packageType is valid
  if (!['Quarter', 'Half', 'Full', 'Premium'].includes(packageType)) {
    console.warn(`Unknown package type: ${packageType}, defaulting to Quarter`);
    packageType = 'Quarter';
    level = level || 'Loudville';
  }

  // Updated package details
  const packageDetails = {
    'Quarter': {
      games: '1 Preseason, 10 Regular Season',
      playoffAccess: 'Early Access to Purchase Playoff Tickets at the discounted member rate',
      savings: 'Quarter plans typically provide an 12% savings over single game pricing.'
    },
    'Half': {
      games: '1 Preseason, 20 Regular Season',
      playoffAccess: 'Early access to purchase tickets in YOUR seats for every OTHER playoff game at the discounted member rate',
      savings: 'Half plans typically provide an 16% savings over single game pricing.'
    },
    'Full': {
      games: '2 Preseason, 41 Regular Season',
      playoffAccess: 'Early access to purchase tickets in YOUR seats for every playoff game at the discounted member rate',
      savings: 'Full plans typically provide an 31% savings over single game pricing.'
    },
    'Premium': {
      games: 'Options vary by locations and clubs.',
      playoffAccess: 'Priority access available with all memberships',
      savings: ''
    }
  };

  // Numeric pricing values with full prices
  const pricingValues = {
    'Lower': {
      'Quarter': { fullMonthlyPrice: 345, discountMonthlyPrice: 328, fullSeatPrice: 4138, discountSeatPrice: 3941 },
      'Half': { fullMonthlyPrice: 191, discountMonthlyPrice: 181, fullSeatPrice: 2287, discountSeatPrice: 2178 },
      'Full': { fullMonthlyPrice: 114, discountMonthlyPrice: 109, fullSeatPrice: 1371, discountSeatPrice: 1305 }
    },
    'Club': {
      'Quarter': { fullMonthlyPrice: 301, discountMonthlyPrice: 287, fullSeatPrice: 3617, discountSeatPrice: 3445},
      'Half': { fullMonthlyPrice: 164, discountMonthlyPrice: 156, fullSeatPrice: 1964, discountSeatPrice: 1870 },
      'Full': { fullMonthlyPrice: 99, discountMonthlyPrice: 94, fullSeatPrice: 1190, discountSeatPrice: 1133 }
    },
    'Loudville': {
      'Quarter': { fullMonthlyPrice: 106, discountMonthlyPrice: 101, fullSeatPrice: 1274, discountSeatPrice: 1213 },
      'Half': { fullMonthlyPrice: 55, discountMonthlyPrice: 53, fullSeatPrice: 665, discountSeatPrice: 633 },
      'Full': { fullMonthlyPrice: 33, discountMonthlyPrice: 31, fullSeatPrice: 394, discountSeatPrice: 375 }
    },
    'Premium': { 
      fullMonthlyPrice: null, 
      discountMonthlyPrice: null, 
      fullSeatPrice: null, 
      discountSeatPrice: null 
    }
  };

  // Create the package name
  let packageName;
  if (packageType === 'Premium') {
    packageName = 'Premium Membership';
  } else {
    packageName = packageType;
    if (level) packageName += ' ' + level;
    packageName += ' Membership';
  }

  // Get numeric pricing for the selected level & package
  let fullMonthlyPrice = null;
  let discountMonthlyPrice = null;
  let fullSeatPrice = null;
  let discountSeatPrice = null;

  if (level && pricingValues[level] && pricingValues[level][packageType]) {
    fullMonthlyPrice = pricingValues[level][packageType].fullMonthlyPrice;
    discountMonthlyPrice = pricingValues[level][packageType].discountMonthlyPrice;
    fullSeatPrice = pricingValues[level][packageType].fullSeatPrice;
    discountSeatPrice = pricingValues[level][packageType].discountSeatPrice;
  } else if (packageType === 'Premium') {
    fullMonthlyPrice = pricingValues['Premium'].fullMonthlyPrice;
    discountMonthlyPrice = pricingValues['Premium'].discountMonthlyPrice;
    fullSeatPrice = pricingValues['Premium'].fullSeatPrice;
    discountSeatPrice = pricingValues['Premium'].discountSeatPrice;
  }

  // Construct dynamic pricing text
  let pricingText = '';
  if (packageType === 'Premium') {
    pricingText = 'Inquire for Pricing';
  } else if (fullMonthlyPrice && discountMonthlyPrice && fullSeatPrice && discountSeatPrice) {
    pricingText = `From $${fullMonthlyPrice} $${discountMonthlyPrice}/mo per seat with 12 monthly payments and 0% interest* Or from $${fullSeatPrice} $${discountSeatPrice} total per seat`;
  } else {
    pricingText = 'Contact for Pricing';
  }

  // Update store
  recommendationStore.set({
    packageName,
    details: {
      games: packageDetails[packageType].games,
      pricing: pricingText,
      fullMonthlyPrice,
      discountMonthlyPrice,
      fullSeatPrice,
      discountSeatPrice,
      savings: packageDetails[packageType].savings,
      playoffAccess: packageDetails[packageType].playoffAccess,
      benefits: 'Ticket Exchange & two selected benefits'
    }
  });

  return {
    packageName,
    details: {
      games: packageDetails[packageType].games,
      pricing: pricingText,
      fullMonthlyPrice,
      discountMonthlyPrice,
      fullSeatPrice,
      discountSeatPrice,
      savings: packageDetails[packageType].savings,
      playoffAccess: packageDetails[packageType].playoffAccess,
      benefits: 'Ticket Exchange & two selected benefits'
    }
  };
}